/**
 * Module de rendu des planètes
 */
import { wheelAngle } from '../core/utils.js';
import { isLightColor } from '../core/utils.js';
import { PLANET_COLORS } from '../core/constants.js';

export class PlanetRenderer {
    /**
     * @param {CanvasRenderingContext2D} ctx - Contexte 2D du canvas
     * @param {Object} options - Options de configuration
     */
    constructor(ctx, options) {
        this.ctx = ctx;
        this.options = options;
        this.planetColors = PLANET_COLORS;
    }

    /**
     * Assombrit une couleur pour créer un effet de dégradé
     * @param {string} color - Couleur hexadécimale
     * @param {number} factor - Facteur d'assombrissement (0-1)
     * @returns {string} Couleur assombrie
     */
    darkenColor(color, factor) {
        const hex = color.replace('#', '');
        const r = Math.floor(parseInt(hex.substr(0, 2), 16) * factor);
        const g = Math.floor(parseInt(hex.substr(2, 2), 16) * factor);
        const b = Math.floor(parseInt(hex.substr(4, 2), 16) * factor);
        return `rgb(${r}, ${g}, ${b})`;
    }

    /**
     * Calcule les positions des planètes selon leurs coordonnées écliptiques
     * @param {number} cx - Coordonnée X du centre
     * @param {number} cy - Coordonnée Y du centre
     * @param {number} radius - Rayon du cercle extérieur
     * @param {Array} planets - Données des planètes
     * @param {Object} planetPositions - Objet à remplir avec les positions calculées
     */
    calculatePositions(cx, cy, radius, planets, planetPositions) {
        if (!planets || !planets.length) return;

        // Définition de l'espace disponible
        const innerRadius = radius * this.options.innerRadius;    // Cercle des maisons (intérieur)
        const outerRadius = radius * this.options.planetRadius;   // Rayon pour les planètes (extérieur)

        // Espace disponible pour placer les planètes
        const availableSpace = outerRadius - innerRadius;

        // Traiter chaque planète selon sa distance configurée
        planets.forEach(planet => {
            const angle = wheelAngle(planet.position, this.options.ascendant);

            // Utiliser directement la distance comme pourcentage de l'espace disponible
            // 0.0 = innerRadius (Lune à l'intérieur)
            // 1.0 = outerRadius (Pluton à l'extérieur)
            const planetRadius = innerRadius + (availableSpace * planet.distance);

            // Stocker les coordonnées et informations de la planète
            planetPositions[planet.name] = {
                x: cx + planetRadius * Math.cos(angle),
                y: cy - planetRadius * Math.sin(angle),
                radius: planetRadius,
                angle: angle,
                symbol: planet.symbol,
                retrograde: planet.retrograde
            };
        });
    }

    /**
     * Dessine les planètes sur le cercle
     * @param {number} cx - Coordonnée X du centre
     * @param {number} cy - Coordonnée Y du centre
     * @param {number} radius - Rayon du cercle extérieur
     * @param {Array} planets - Données des planètes
     * @param {Object} planetPositions - Positions précalculées des planètes
     */
    draw(cx, cy, radius, planets, planetPositions) {
        if (!planets || !planets.length) return;

        const ctx = this.ctx;

        // Dessiner chaque planète à partir des positions précalculées
        planets.forEach(planet => {
            const planetInfo = planetPositions[planet.name];
            if (!planetInfo) return;

            const { x, y } = planetInfo;

            // Taille du symbole proportionnelle au rayon
            const symbolSize = Math.max(20, Math.round(radius * 0.07));
            const circleSize = symbolSize * 0.7; // Réduire davantage la taille du cercle

            // Cercle coloré selon la planète - Style Art Déco
            const planetColor = this.planetColors[planet.name] || '#d85f28';

            // Dégradé radial pour un effet Art Déco
            const gradient = ctx.createRadialGradient(x - circleSize * 0.3, y - circleSize * 0.3, 0, x, y, circleSize);
            gradient.addColorStop(0, planetColor);
            gradient.addColorStop(1, this.darkenColor(planetColor, 0.8));

            ctx.beginPath();
            ctx.arc(x, y, circleSize, 0, Math.PI * 2);
            ctx.fillStyle = gradient;
            ctx.fill();

            // Bordure dorée Art Déco
            ctx.strokeStyle = 'rgba(230, 191, 164, 0.8)';  // line-strong
            ctx.lineWidth = 1.5;
            ctx.stroke();

            // Symbole de la planète en blanc ou noir selon la couleur de fond
            const textColor = isLightColor(planetColor) ? '#1b2725' : '#fafaf5';
            ctx.font = `${symbolSize}px "Noto Sans Symbols", sans-serif`;
            ctx.fillStyle = textColor;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(planet.symbol, x, y);

            // Ajouter un symbole R pour les planètes rétrogrades
            if (planet.retrograde) {
                // Rapprocher le R du symbole
                const rxOffset = circleSize * 0.8;
                ctx.font = `${Math.max(10, Math.round(symbolSize * 0.5))}px Arial`;
                ctx.fillText('R', x + rxOffset, y);
            }
        });
    }
}